import { useCallback, useEffect, useRef, useState } from "react";

import { useRootDispatch, useTriggerIFrame } from "@ds/hooks";

import { toastShowErrorAction } from "@ds/modules/notifications/redux/actions";

import { isErrorLike } from "@ds/utils/type-guards/error-guards";

import { deploymentsService } from "../utils/api";

export const useGenerateDeploymentReport = (deploymentIds: number[]): [() => Promise<void>, boolean] => {
  const dispatch = useRootDispatch();

  const deploymentIdsRef = useRef(deploymentIds);
  const inFlightRef = useRef(false);

  const [isLoading, setIsLoading] = useState(false);

  const { triggerIframe } = useTriggerIFrame();

  // no dependencies needed for this effect - keeps the ref updated with the latest
  // deployment ids prop
  useEffect(() => {
    deploymentIdsRef.current = deploymentIds;
  });

  const generateDeploymentReport = useCallback(async () => {
    if (inFlightRef.current) return;

    inFlightRef.current = true;
    setIsLoading(true);

    try {
      const response = await deploymentsService.getDeploymentReportURL(deploymentIdsRef.current);

      if (response?.presigned_url) {
        // Leveraging an iframe prevents pop-up blocking and allows for the report
        // to be downloaded without the need for a new tab.
        await triggerIframe(response.presigned_url);
      } else {
        const serverMsg = response?.message ?? "Report URL not found";
        dispatch(toastShowErrorAction(new Error(serverMsg), "Report Generation Failed"));
      }
    } catch (error) {
      dispatch(toastShowErrorAction(isErrorLike(error) ? error : new Error(String(error)), "Report Generation Failed"));
    } finally {
      inFlightRef.current = false;
      setIsLoading(false);
    }
  }, [dispatch, triggerIframe]);

  return [generateDeploymentReport, isLoading];
};
